import React, { useState, useEffect, useRef } from "react";
import { getAccounts, createAccount, deleteAccount, getZerodhaLogin, getBreezeLogin, disconnectBroker, angelLogin, importHoldingsCsv, listManualHoldings, patchManualHolding, deleteManualHolding, addManualHolding, refreshAccountPrices, dedupAccounts, getConnectionHealth } from "../lib/api";
import { Plus, Trash2, Link2, Unlink, Landmark, Loader2, CheckCircle2, XCircle, Clock, Key, Upload, Edit2, X, Save, RefreshCw, Activity, AlertTriangle, ShieldCheck, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { getBackendUrl } from "../lib/backendUrl";

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [angelOpen, setAngelOpen] = useState(false);
  const [angelAccountId, setAngelAccountId] = useState("");
  const [angelClientId, setAngelClientId] = useState("");
  const [angelPassword, setAngelPassword] = useState("");
  const [angelTotp, setAngelTotp] = useState("");
  const [angelLoading, setAngelLoading] = useState(false);
  const [angelError, setAngelError] = useState("");
  const [broker, setBroker] = useState("zerodha");
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [importingId, setImportingId] = useState(null);
  const [importMsg, setImportMsg] = useState({});
  const [editHoldingsAcc, setEditHoldingsAcc] = useState(null);
  const fileInputs = useRef({});

  const load = async () => {
    try {
      const { data } = await getAccounts();
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error("Accounts load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    // For manual accounts, backend accepts empty api_key/secret and treats
    // the account as user-editable via Edit Holdings. Otherwise (Zerodha /
    // ICICI / Angel) we require an API key.
    await createAccount({ broker, account_name: name, api_key: apiKey, api_secret: apiSecret });
    setOpen(false);
    setBroker("zerodha"); setName(""); setApiKey(""); setApiSecret("");
    load();
  };

  const handleConnect = async (acc) => {
    try {
      if (acc.broker === "zerodha") {
        const { data } = await getZerodhaLogin(acc.id);
        window.location.href = data.login_url;
      } else if (acc.broker === "icici") {
        const { data } = await getBreezeLogin(acc.id);
        window.location.href = data.login_url;
      } else if (acc.broker === "angel") {
        setAngelAccountId(acc.id);
        setAngelError("");
        setAngelOpen(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to get broker login URL");
    }
  };

  const handleAngelLogin = async () => {
    setAngelLoading(true);
    setAngelError("");
    try {
      await angelLogin({
        account_id: angelAccountId,
        client_id: angelClientId,
        password: angelPassword,
        totp_secret: angelTotp,
      });
      setAngelOpen(false);
      setAngelClientId(""); setAngelPassword(""); setAngelTotp("");
      load();
    } catch (err) {
      setAngelError(err.response?.data?.detail || "Login failed");
    } finally {
      setAngelLoading(false);
    }
  };

  const handleDisconnect = async (acc) => {
    await disconnectBroker(acc.id);
    load();
  };

  const handleDelete = async (acc) => {
    if (window.confirm(`Delete ${acc.account_name}?`)) {
      await deleteAccount(acc.id);
      load();
    }
  };

  const handleCsvImport = async (acc, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportingId(acc.id);
    setImportMsg(m => ({ ...m, [acc.id]: "" }));
    try {
      const { data } = await importHoldingsCsv(acc.id, file, "replace");
      setImportMsg(m => ({ ...m, [acc.id]: `✓ Imported ${data.count} holdings` }));
      setTimeout(() => load(), 500);
    } catch (err) {
      setImportMsg(m => ({ ...m, [acc.id]: `✗ ${err.response?.data?.detail || "Import failed"}` }));
    } finally {
      setImportingId(null);
      event.target.value = ""; // allow re-upload same file
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6" data-testid="accounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Broker Accounts</h1>
          <p className="text-sm text-[var(--text-muted)]">Manage your connected brokerage accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="text-xs border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-[var(--brand)]/40"
            onClick={async () => {
              if (!window.confirm("Merge duplicate accounts?\nAccounts with the same name will be merged. Holdings are reassigned to the canonical account — no data is deleted.")) return;
              try {
                const { data } = await dedupAccounts();
                if (data.merged_count === 0) {
                  toast.success("No duplicates found — everything looks clean.");
                } else {
                  toast.success(`Merged ${data.merged_count} duplicate account${data.merged_count === 1 ? "" : "s"}. Refreshing…`);
                }
                setTimeout(() => load(), 400);
              } catch (err) {
                toast.error("Dedup failed: " + (err.response?.data?.detail || err.message));
              }
            }}
            data-testid="merge-duplicates-btn"
          >
            Merge Duplicates
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded text-sm" data-testid="add-account-btn">
              <Plus size={16} className="mr-1" /> Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[var(--bg-surface)] border border-white/10 text-white max-w-md" data-testid="add-account-dialog">
            <DialogHeader><DialogTitle className="font-bold" style={{ fontFamily: "Chivo" }}>Add Broker Account</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">Broker</label>
                <Select value={broker} onValueChange={setBroker}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                    <SelectItem value="manual">Manual — Indian Equity (no API key)</SelectItem>
                    <SelectItem value="zerodha">Zerodha</SelectItem>
                    <SelectItem value="icici">ICICI Direct</SelectItem>
                    <SelectItem value="angel">Angel One</SelectItem>
                  </SelectContent>
                </Select>
                {broker === "manual" && (
                  <p className="text-[11px] text-[var(--text-muted)] mt-1.5 leading-relaxed">
                    Track any Indian stock manually — enter ticker, quantity, and average price via <span className="text-[var(--brand)]">Edit Holdings</span> after creating the account. CMP updates automatically from yfinance (NSE) on each Refresh.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">Account Name</label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder={broker === "manual" ? "e.g. Anya - Zerodha" : "e.g. Zerodha Primary"} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="account-name-input" />
              </div>
              {broker !== "manual" && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">API Key</label>
                    <Input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="API Key" className="bg-[var(--bg-base)] border-white/10 text-white font-mono" data-testid="api-key-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">API Secret</label>
                    <Input value={apiSecret} onChange={e => setApiSecret(e.target.value)} placeholder="API Secret" type="password" className="bg-[var(--bg-base)] border-white/10 text-white font-mono" data-testid="api-secret-input" />
                  </div>
                </>
              )}
              <Button onClick={handleCreate} disabled={!name || (broker !== "manual" && !apiKey)} className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded" data-testid="create-account-submit">
                Create Account
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Connection Health Widget */}
      <ConnectionHealthWidget />

      {/* Account Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map((acc) => {
          const ss = acc.sync_status;
          const isLiveBroker = ["zerodha", "icici", "angel"].includes(acc.broker);
          const connected = isLiveBroker && (ss?.status === "connected" || ss?.status === "synced");
          const snapshotLoaded = !isLiveBroker && ["connected", "synced", "imported"].includes(ss?.status);
          return (
            <div key={acc.id} className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid={`account-${acc.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Landmark size={20} className="text-[var(--text-muted)]" />
                  <div>
                    <div className="text-sm font-medium text-white">{acc.account_name}</div>
                    <div className="text-xs text-[var(--text-muted)] font-mono">{acc.broker.toUpperCase()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {connected ? (
                    <span className="flex items-center gap-1 text-xs text-[var(--positive)]">
                      <CheckCircle2 size={14} /> Connected
                    </span>
                  ) : snapshotLoaded ? (
                    <span className="flex items-center gap-1 text-xs text-[var(--positive)]">
                      <CheckCircle2 size={14} /> Snapshot loaded
                    </span>
                  ) : ss?.status === "error" ? (
                    <span className="flex items-center gap-1 text-xs text-[var(--negative)]">
                      <XCircle size={14} /> Error
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                      <Clock size={14} /> Not connected
                    </span>
                  )}
                </div>
              </div>

              {ss?.last_sync && (
                <div className="text-[10px] text-[var(--text-muted)] font-mono mb-3">
                  {isLiveBroker ? "Last sync" : "Last update"}: {new Date(ss.last_sync).toLocaleString()} | {ss.records || 0} records
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                {(acc.broker === "zerodha" || acc.broker === "icici" || acc.broker === "angel") && (
                  connected ? (
                    <Button onClick={() => handleDisconnect(acc)} className="bg-transparent border border-white/10 text-[var(--text-secondary)] hover:text-[var(--negative)] hover:border-[var(--negative)]/30 text-xs px-3 py-1 rounded" data-testid={`disconnect-${acc.id}`}>
                      <Unlink size={14} className="mr-1" /> Disconnect
                    </Button>
                  ) : (
                    <Button onClick={() => handleConnect(acc)} className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-xs px-3 py-1 rounded" data-testid={`connect-${acc.id}`}>
                      <Link2 size={14} className="mr-1" /> Connect
                    </Button>
                  )
                )}
                {(acc.broker === "manual" || acc.manual || acc.broker === "mutual_fund" || acc.broker === "us_stocks") && (
                  <>
                    <input
                      ref={el => (fileInputs.current[acc.id] = el)}
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleCsvImport(acc, e)}
                      className="hidden"
                      data-testid={`csv-file-${acc.id}`}
                    />
                    <Button
                      onClick={() => setEditHoldingsAcc(acc)}
                      className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-xs px-3 py-1 rounded"
                      data-testid={`edit-holdings-${acc.id}`}
                    >
                      <Edit2 size={14} className="mr-1" /> Edit Holdings
                    </Button>
                    <Button
                      onClick={() => fileInputs.current[acc.id]?.click()}
                      disabled={importingId === acc.id}
                      className="bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs px-3 py-1 rounded"
                      data-testid={`import-csv-${acc.id}`}
                    >
                      {importingId === acc.id ? (
                        <><Loader2 size={14} className="animate-spin mr-1" /> Importing...</>
                      ) : (
                        <><Upload size={14} className="mr-1" /> Import CSV</>
                      )}
                    </Button>
                  </>
                )}
                <Button onClick={() => handleDelete(acc)} className="bg-transparent border border-white/10 text-[var(--text-muted)] hover:text-[var(--negative)] hover:border-[var(--negative)]/30 text-xs px-3 py-1 rounded" data-testid={`delete-${acc.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
              {importMsg[acc.id] && (
                <div className={`mt-2 text-[11px] font-mono ${importMsg[acc.id].startsWith("✓") ? "text-[var(--positive)]" : "text-[var(--negative)]"}`} data-testid={`import-msg-${acc.id}`}>
                  {importMsg[acc.id]}
                </div>
              )}
              {(acc.broker === "manual" || acc.manual) && (
                <p className="text-[10px] text-[var(--text-muted)] mt-2 leading-relaxed">
                  Upload your Zerodha Console <span className="font-mono text-[var(--text-secondary)]">holdings.csv</span> (Instrument, Qty., Avg. cost, LTP, Cur. val). All existing holdings will be replaced with the CSV data.
                </p>
              )}
              {acc.broker === "us_stocks" && (
                <p className="text-[10px] text-[var(--text-muted)] mt-2 leading-relaxed">
                  Snapshot-based account — holdings update through a verified import or manual edit, not a live broker API connection.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Angel One Login Dialog */}
      <Dialog open={angelOpen} onOpenChange={setAngelOpen}>
        <DialogContent className="bg-[var(--bg-surface)] border border-white/10 text-white max-w-md" data-testid="angel-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <Key size={18} className="text-[var(--brand)]" /> Connect Angel One
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {angelError && (
              <div className="bg-[var(--negative)]/10 border border-[var(--negative)]/20 text-[var(--negative)] text-sm rounded p-2.5" data-testid="angel-error">
                {angelError}
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">Client ID</label>
              <Input value={angelClientId} onChange={e => setAngelClientId(e.target.value)} placeholder="e.g. A12345" className="bg-[var(--bg-base)] border-white/10 text-white font-mono" data-testid="angel-client-id" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">Password</label>
              <Input type="password" value={angelPassword} onChange={e => setAngelPassword(e.target.value)} placeholder="Trading password" className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="angel-password" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block">TOTP Secret Key</label>
              <Input value={angelTotp} onChange={e => setAngelTotp(e.target.value)} placeholder="Secret from QR code setup" className="bg-[var(--bg-base)] border-white/10 text-white font-mono text-xs" data-testid="angel-totp" />
              <p className="text-[10px] text-[var(--text-muted)] mt-1">The secret key from TOTP setup (not the 6-digit code). Auto-generates codes.</p>
            </div>
            <Button onClick={handleAngelLogin} disabled={angelLoading || !angelClientId || !angelPassword || !angelTotp}
              className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded" data-testid="angel-login-submit">
              {angelLoading ? <><Loader2 size={14} className="animate-spin mr-1" />Connecting...</> : "Connect Angel One"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editHoldingsAcc && (
        <EditHoldingsModal account={editHoldingsAcc} onClose={() => { setEditHoldingsAcc(null); load(); }} />
      )}
    </div>
  );
}


function EditHoldingsModal({ account, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newRow, setNewRow] = useState({ symbol: "", quantity: "", avg_price: "", last_price: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listManualHoldings(account.id);
      setRows((data || []).map(h => ({
        ...h,
        _sym: h.tradingsymbol || "",
        _qty: String(h.quantity || ""),
        _avg: String(h.average_price || ""),
        _ltp: String(h.last_price || ""),
      })));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSave = async (row) => {
    setSaving(row.tradingsymbol);
    try {
      const newSym = (row._sym || "").trim().toUpperCase();
      const renamed = newSym && newSym !== row.tradingsymbol.toUpperCase();
      await patchManualHolding(account.id, row.tradingsymbol, {
        quantity: parseFloat(row._qty) || 0,
        avg_price: parseFloat(row._avg) || 0,
        last_price: parseFloat(row._ltp) || 0,
        ...(renamed ? { new_symbol: newSym } : {}),
      });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      // eslint-disable-next-line no-alert
      window.alert(msg);
    }
    setSaving(null);
  };

  const handleDelete = async (sym) => {
    if (!window.confirm(`Delete ${sym}?`)) return;
    await deleteManualHolding(account.id, sym);
    load();
  };

  const handleAdd = async () => {
    if (!newRow.symbol || !newRow.quantity) return;
    setSaving("__new__");
    try {
      await addManualHolding(account.id, {
        symbol: newRow.symbol.toUpperCase(),
        quantity: parseFloat(newRow.quantity) || 0,
        avg_price: parseFloat(newRow.avg_price) || 0,
      });
      // If a CMP was entered, patch it
      if (newRow.last_price) {
        await patchManualHolding(account.id, newRow.symbol.toUpperCase(), {
          last_price: parseFloat(newRow.last_price) || 0,
        });
      }
      setNewRow({ symbol: "", quantity: "", avg_price: "", last_price: "" });
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to add holding";
      // eslint-disable-next-line no-alert
      window.alert(`Could not add holding: ${msg}`);
    }
    setSaving(null);
  };

  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try {
      const { data } = await refreshAccountPrices(account.id);
      const refreshed = data?.refreshed ?? 0;
      const total = data?.total ?? rows.length;
      const failed = data?.failed || [];
      if (failed.length > 0) {
        const failNames = failed.slice(0, 5).map(f => f.symbol).join(", ");
        toast.warning(`Refreshed ${refreshed}/${total}. Couldn't fetch: ${failNames}${failed.length > 5 ? "…" : ""}`);
      } else {
        toast.success(`Refreshed prices for ${refreshed} holding${refreshed === 1 ? "" : "s"}`);
      }
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Refresh failed";
      toast.error(`Refresh failed: ${msg}`);
    }
    setRefreshing(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-holdings-modal">
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "Chivo" }}>Edit Holdings — {account.account_name}</h2>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Manually adjust quantity, average price, and current market price (CMP) for each holding.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshPrices}
              disabled={refreshing || loading || rows.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-[var(--brand)]/30 bg-[var(--brand)]/10 hover:bg-[var(--brand)]/20 hover:border-[var(--brand)]/50 transition-colors text-xs uppercase tracking-wider text-[var(--brand)] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              title="Fetch latest CMP from yfinance / AMFI for all holdings in this account"
              data-testid="refresh-prices-btn"
            >
              {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {refreshing ? "Refreshing…" : "Refresh Prices"}
            </button>
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-white p-1 rounded" data-testid="edit-holdings-close"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[var(--brand)]" /></div>
          ) : (
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="holdings-edit-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Symbol</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Quantity</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Avg Price (₹)</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">CMP (₹)</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Value</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const qty = parseFloat(r._qty) || 0;
                      const ltp = parseFloat(r._ltp) || 0;
                      const value = qty * ltp;
                      return (
                        <tr key={i} className="border-b border-white/5" data-testid={`row-${r.tradingsymbol}`}>
                          <td className="py-1 px-2">
                            <input
                              type="text"
                              value={r._sym}
                              onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => j === i ? { ...x, _sym: v } : x)); }}
                              className="w-full min-w-[180px] bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white font-mono"
                              data-testid={`sym-${r.tradingsymbol}`}
                            />
                          </td>
                          <td className="py-1 px-2"><input type="number" value={r._qty} onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => j === i ? { ...x, _qty: v } : x)); }} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid={`qty-${r.tradingsymbol}`} /></td>
                          <td className="py-1 px-2"><input type="number" value={r._avg} onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => j === i ? { ...x, _avg: v } : x)); }} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid={`avg-${r.tradingsymbol}`} /></td>
                          <td className="py-1 px-2"><input type="number" value={r._ltp} onChange={e => { const v = e.target.value; setRows(rs => rs.map((x, j) => j === i ? { ...x, _ltp: v } : x)); }} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid={`ltp-${r.tradingsymbol}`} /></td>
                          <td className="py-2 px-2 text-right font-mono text-xs text-[var(--text-secondary)]">₹{value.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          <td className="py-2 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => handleSave(r)} disabled={saving === r.tradingsymbol} className="p-1.5 rounded bg-[var(--brand)]/15 text-[var(--brand)] hover:bg-[var(--brand)]/25 disabled:opacity-40" data-testid={`save-${r.tradingsymbol}`}>
                                {saving === r.tradingsymbol ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                              </button>
                              <button onClick={() => handleDelete(r.tradingsymbol)} className="p-1.5 rounded text-[var(--text-muted)] hover:text-red-400" data-testid={`del-${r.tradingsymbol}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {/* New row */}
                    <tr className="bg-white/[0.02]" data-testid="new-row">
                      <td className="py-1 px-2"><input type="text" placeholder="SYMBOL" value={newRow.symbol} onChange={e => setNewRow({ ...newRow, symbol: e.target.value.toUpperCase() })} className="w-28 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white font-mono" data-testid="new-symbol" /></td>
                      <td className="py-1 px-2"><input type="number" placeholder="Qty" value={newRow.quantity} onChange={e => setNewRow({ ...newRow, quantity: e.target.value })} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid="new-qty" /></td>
                      <td className="py-1 px-2"><input type="number" placeholder="Avg" value={newRow.avg_price} onChange={e => setNewRow({ ...newRow, avg_price: e.target.value })} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid="new-avg" /></td>
                      <td className="py-1 px-2"><input type="number" placeholder="CMP" value={newRow.last_price} onChange={e => setNewRow({ ...newRow, last_price: e.target.value })} className="w-24 bg-[var(--bg-base)] border border-white/10 rounded px-2 py-1 text-xs text-white text-right font-mono" data-testid="new-ltp" /></td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-[var(--text-muted)]">—</td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={handleAdd} disabled={!newRow.symbol || !newRow.quantity || saving === "__new__"} className="px-2 py-1 rounded bg-[var(--brand)] text-white text-xs font-medium hover:bg-[var(--brand-hover)] disabled:opacity-40" data-testid="add-new-holding">
                          {saving === "__new__" ? <Loader2 size={12} className="animate-spin" /> : <><Plus size={12} className="inline mr-0.5" />Add</>}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-[var(--text-muted)] leading-relaxed pt-2">
                <span className="text-white">Tip:</span> You can leave CMP as-is (it'll auto-update from Yahoo Finance in the background), or set it manually here to override. All edits persist to the portfolio immediately.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const STATUS_META = {
  fresh: { color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: ShieldCheck, label: "Fresh today" },
  stale: { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Clock, label: "Stale" },
  expired: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: AlertTriangle, label: "Expired" },
  missing: { color: "text-[var(--text-muted)]", bg: "bg-white/5", border: "border-white/10", icon: XCircle, label: "Not connected" },
};

function ConnectionHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getConnectionHealth();
      setData(res.data);
    } catch (err) {
      console.error("health load failed", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleReconnect = async (b) => {
    if (!b.reconnect) return;
    setBusyId(b.account_id);
    try {
      const url = `${getBackendUrl()}${b.reconnect.endpoint}`;
      if (b.reconnect.type === "manual_login") {
        const res = await axios.get(url, { withCredentials: true });
        const loginUrl = res?.data?.login_url;
        if (loginUrl) window.location.href = loginUrl;
        else toast.error("Could not get ICICI login URL");
      } else {
        const res = await axios.post(url, {}, { withCredentials: true });
        const ok = res?.data?.ok ?? true;
        if (ok) {
          if (b.reconnect.type === "telegram_reminder") {
            toast.success(res.data?.skipped ? "Already sent today — check Telegram" : "Re-login link sent to Telegram");
          } else {
            toast.success(`${b.account_name} refreshed`);
          }
          await load();
        } else {
          toast.error(res?.data?.error || "Reconnect failed");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Reconnect failed");
    } finally {
      setBusyId(null);
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    const now = new Date();
    const hrs = (now - d) / 3600000;
    if (hrs < 1) return `${Math.round(hrs * 60)}m ago`;
    if (hrs < 24) return `${Math.round(hrs)}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4 animate-pulse" data-testid="connection-health-loading">
        <div className="h-4 w-40 bg-white/10 rounded mb-3" />
        <div className="h-12 bg-white/5 rounded" />
      </div>
    );
  }
  const brokers = data?.brokers || [];
  if (brokers.length === 0) return null;

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="connection-health">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[var(--brand)]" />
          <h2 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>Connection health</h2>
          <span className="text-[10px] text-[var(--text-muted)]">· daily-session brokers only</span>
        </div>
        <button
          onClick={load}
          className="p-1.5 text-[var(--text-muted)] hover:text-white rounded hover:bg-white/5"
          title="Refresh"
          data-testid="health-refresh-btn"
        >
          <RefreshCw size={12} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {brokers.map((b) => {
          const meta = STATUS_META[b.status] || STATUS_META.missing;
          const Icon = meta.icon;
          const ReconIcon = b.reconnect?.type === "telegram_reminder" ? Send : RefreshCw;
          const showReconnect = b.status !== "fresh" && b.reconnect;
          return (
            <div key={b.account_id} className={`p-3 rounded border ${meta.border} ${meta.bg}`} data-testid={`health-${b.broker}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5">
                  <Icon size={13} className={meta.color} />
                  <span className="text-xs font-semibold text-white capitalize">{b.broker}</span>
                  {b.auto_login && (
                    <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-[var(--brand)]/15 text-[var(--brand)]">auto</span>
                  )}
                </div>
                <span className={`text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                <span className="text-white/70">{b.account_name}</span>
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                Last: <span className="font-mono text-white/70">{fmtTime(b.last_session_at)}</span>
              </div>
              {showReconnect && (
                <button
                  onClick={() => handleReconnect(b)}
                  disabled={busyId === b.account_id}
                  className={`mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] rounded border ${meta.border} ${meta.color} hover:bg-white/5 transition-colors disabled:opacity-50`}
                  data-testid={`health-reconnect-${b.broker}`}
                >
                  <ReconIcon size={11} className={busyId === b.account_id ? "animate-spin" : ""} />
                  {busyId === b.account_id ? "Working…" : b.reconnect.label}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
